import React from "react";
import Xmark from "../../components/ui/Icon/FontAwesome/Regular/Sharp/Xmark";
import { MagnifyingGlass } from "../../components/ui/Icon/FontAwesome/Regular/Sharp";

export interface SearchBoxUIProps {
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  placeholder: string;
  isFocused: boolean;
  searchUri?: string;
  activeQuery?: string;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** When true, the magnifying glass is a plain icon; when false it links to the catalog */
  isCatalog: boolean;
  showTooltip?: boolean;
}

export default function SearchBoxUI({
  inputRef,
  value,
  placeholder,
  isFocused,
  searchUri = "c",
  activeQuery,
  onFocus,
  onBlur,
  onChange,
  onClear,
  onKeyDown,
  isCatalog,
  showTooltip = true,
}: SearchBoxUIProps) {
  return (
    <div className="tw:grow tw:self-stretch">
      <div className="tw:relative tw:flex tw:h-full tw:w-full tw:items-center tw:justify-between tw:rounded-sm tw:bg-gray-100 tw:*:grow tw:outline tw:outline-gray-200">
        <label
          htmlFor="search-input"
          className="tw:sr-only tw:text-base tw:text-gray-500"
        >
          {placeholder}
        </label>
        <input
          ref={inputRef}
          type="search"
          id="search-input"
          name="query"
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          onFocus={onFocus}
          onBlur={onBlur}
          defaultValue={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="tw:w-full tw:bg-transparent tw:py-3.75 tw:pr-12.75 tw:pl-4 tw:text-sm tw:placeholder:text-sm tw:placeholder:text-gray-700 tw:xl:placeholder:text-base"
        />
        <div
          className={`tw:absolute tw:right-0 tw:mr-4 tw:flex tw:h-full tw:items-center ${showTooltip ? "tw:gap-2" : "tw:gap-4"} tw:py-3`}
        >
          {!value && !isFocused && showTooltip && (
            <div className="tw:group/hint tw:relative tw:hidden tw:xl:block">
              <kbd className="tw:flex tw:cursor-default tw:items-center tw:rounded tw:border tw:border-gray-900/15 tw:bg-white tw:px-1.5 tw:font-sans tw:text-xs tw:text-gray-400 tw:shadow-xs">
                /
              </kbd>
              <div
                role="tooltip"
                className="tw:pointer-events-none tw:absolute tw:bottom-full tw:left-1/2 tw:mb-2 tw:-translate-x-1/2 tw:rounded tw:bg-gray-900 tw:px-2 tw:py-1 tw:text-xs tw:whitespace-nowrap tw:text-white tw:opacity-0 tw:transition-opacity tw:group-hover/hint:opacity-100"
              >
                {Craft.t("js", "Press / to start searching")}
                <div className="tw:absolute tw:top-full tw:left-1/2 tw:-translate-x-1/2 tw:border-4 tw:border-transparent tw:border-t-gray-900" />
              </div>
            </div>
          )}
          {value && (
            <button
              type="button"
              onClick={onClear}
              aria-label={Craft.t("js", "Clear search")}
              className="tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:text-gray-400 tw:transition-colors tw:hover:text-gray-700"
            >
              <Xmark iconClass="tw:size-4.5 tw:fill-current" />
            </button>
          )}
          <div className="tw:h-full tw:w-px tw:rounded-sm tw:bg-gray-900/20" />
          {isCatalog ? (
            <MagnifyingGlass iconClass="tw:size-4.5 tw:cursor-pointer" />
          ) : (
            <a href={Craft.getSiteUrl(searchUri, { query: activeQuery })}>
              <MagnifyingGlass iconClass="tw:size-4.5 tw:cursor-pointer" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
